import { useRef, useState } from "react";
import { updateUserProfile } from "../services/userService";
import { getApiErrorMessage } from "../utils/Util";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  userId: string;
  onSuccess: () => void;
}

/** 0 ile başlıyorsa baştaki 0'ı sil, her durumda +90 ekle */
export const formatPhoneForApi = (raw: string): string => {
  const digits = raw.replace(/\D/g, "");
  const stripped = digits.startsWith("0") ? digits.slice(1) : digits;
  return `+90${stripped}`;
};

function PhoneVerificationPopup({ userId, onSuccess }: Props) {
  const { t } = useLanguage();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  /** Sadece rakam, max 11 hane */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 11);
    setPhone(val);
    if (error) setError("");
  };

  /** 0 ile başlıyorsa 11, başlamıyorsa 10 rakam beklenir */
  const isValid = (() => {
    if (phone.startsWith("0")) return phone.length === 11;
    return phone.length === 10;
  })();

  const formattedPreview = phone.length > 0 ? formatPhoneForApi(phone) : "";

  const handleSubmit = async () => {
    if (!isValid) {
      setError(t("phoneVerification.invalid"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      await updateUserProfile(userId, { customerTelNo: formatPhoneForApi(phone) });
      closeRef.current?.click();
      onSuccess();
    } catch (err) {
      setError(getApiErrorMessage(err, t("phoneVerification.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal fade"
      id="phoneVerificationModal"
      tabIndex={-1}
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "380px" }}>
        <div className="modal-content rounded-4 shadow">
          <div className="modal-header background-red">
            <h5 className="modal-title text-white">
              <i className="bi bi-telephone-fill me-2"></i>
              {t("phoneVerification.title")}
            </h5>
            <button
              ref={closeRef}
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label={t("common.close")}
            />
          </div>

          <div className="modal-body px-4 py-4">
            <p className="text-muted small mb-4">
              {t("phoneVerification.hint")}
            </p>

            <label className="form-label fw-semibold small mb-1">
              {t("phoneVerification.label")} <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span
                className="input-group-text fw-bold"
                style={{ backgroundColor: "#f8f9fa", minWidth: "56px", justifyContent: "center" }}
              >
                +90
              </span>
              <input
                type="tel"
                className={`form-control${error ? " is-invalid" : ""}`}
                placeholder="5XX XXX XX XX"
                value={phone}
                onChange={handleChange}
                inputMode="numeric"
                autoFocus
              />
              {error && <div className="invalid-feedback">{error}</div>}
            </div>

            {/* Önizleme */}
            {phone.length > 0 && (
              <div className="mt-2 small text-muted">
                <i className="bi bi-send me-1"></i>
                {t("phoneVerification.savedNumber")}{" "}
                <span className={`fw-semibold ${isValid ? "text-success" : "text-danger"}`}>
                  {formattedPreview}
                </span>
              </div>
            )}

            <div className="text-muted mt-2" style={{ fontSize: "0.75rem" }}>
              Örnek: <strong>05331234567</strong> veya <strong>5331234567</strong>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-danger w-100 rounded-pill py-2 fw-semibold"
              onClick={handleSubmit}
              disabled={!isValid || saving}
            >
              {saving ? (
                <span className="spinner-border spinner-border-sm me-2" />
              ) : (
                <i className="bi bi-check-circle me-2"></i>
              )}
              {t("phoneVerification.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhoneVerificationPopup;
